/**
 * Copyright © 2026 Adnuntius AS.
 */
import {ChangeDetectorRef, Component, Inject, Input, NgZone, OnChanges, OnInit, SimpleChanges} from '@angular/core';
import {NgForm} from '@angular/forms';
import * as _ from 'lodash';
import template from './report-schedules.component.html';

import {OBJECT_STATE} from '../../../components/api/resources/resources-constants';
import {Uuid} from '../../../components/util/uuid';
import {EmailHelper} from '../../../components/util/email';
import {ADN_REPORT_TYPES} from '../report-types';
import {afterSaveAfterInitMods, beforeSubmitMods} from '../../report-schedule/report-schedule-commons';
import {ReportScheduleModalService} from '../../report-schedule/report-schedule-modal.service';
import {OrderResource} from '../../../components/api/resources/resource/order.resource';
import {ReportScheduleResource} from '../../../components/api/resources/resource/report-schedule.resource';
import {ReportTemplateResource} from '../../../components/api/resources/resource/report-template.resource';
import {UserResource} from '../../../components/api/resources/resource/user.resource';
import {AdnTimeShifterService} from '../../../components/util/adn-time-shifter.service';
import {LocalNetworkProfile} from '../../../components/session/local-network-profile';
import {LocalUserProfile} from '../../../components/session/local-user-profile';
import {deferMarkForCheck} from '../../common/util/defer-mark-for-check';


@Component({
  standalone: false,
  selector: 'adn-report-schedules',
  template
})
export class ReportSchedulesComponent implements OnInit, OnChanges {
  @Input() obj: any;
  @Input() reportType = 'LINE_ITEM';
  @Input() currency: string;

  reportTemplates: any[];
  lineItemReportTemplates: any[] = [];
  reportSchedules: any[] = [];
  reportSchedule: any;
  schedulePeriods: any[] = [];
  availableCurrencies: string[] = [];

  showReportSchedule = false;
  additionHappened = false;
  editingScheduleId: string | null = null;
  saving = false;

  private adnReportType: any;
  private readonly timeZone: string;
  private readonly scheduleLocale: string;
  private readonly preferredCurrency: string;

  constructor(
    @Inject(AdnTimeShifterService) private readonly adnTimeShifter: any,
    @Inject(UserResource) private readonly User: any,
    @Inject(OrderResource) private readonly Order: any,
    @Inject(ReportScheduleResource) private readonly ReportSchedule: any,
    @Inject(ReportTemplateResource) private readonly ReportTemplate: any,
    @Inject(LocalUserProfile) localUserProfile: any,
    @Inject(LocalNetworkProfile) private readonly LocalNetworkProfile: any,
    private readonly reportScheduleModalService: ReportScheduleModalService,
    private readonly ngZone: NgZone,
    private readonly cdr: ChangeDetectorRef
  ) {
    this.timeZone = LocalNetworkProfile.get('timeZone') || 'utc';
    this.scheduleLocale = localUserProfile.get('locale') || 'en_GB';
    this.preferredCurrency = localUserProfile.getPreferredCurrency();
  }

  ngOnInit(): void {
    this.ensureReportTypeInitialized();
    this.availableCurrencies = this.LocalNetworkProfile.get('availableCurrencies') || [];

    this.ReportTemplate.query().$promise.then((page: any) => {
      // undefined → [] flips [resolved] on adn-promise-spinner; defer to avoid NG0100.
      deferMarkForCheck(this.cdr, () => {
        this.reportTemplates = _.filter(page.results, {reportType: this.adnReportType.id});
        this.lineItemReportTemplates = _.filter(page.results, {reportType: 'LINE_ITEM'});
      });
    });

    this.loadIfReady();
  }

  ngOnChanges(changes: SimpleChanges): void {
    this.ensureReportTypeInitialized();
    if (!this.adnReportType) {
      return;
    }

    if (changes.reportType) {
      if (this.reportTemplates && this.adnReportType) {
        this.ReportTemplate.query().$promise.then((page: any) => {
          this.ngZone.run(() => {
            this.reportTemplates = _.filter(page.results, {reportType: this.adnReportType.id});
            this.lineItemReportTemplates = _.filter(page.results, {reportType: 'LINE_ITEM'});
          });
        });
      }
    }

    if (changes.obj || changes.currency || changes.reportType) {
      const previousId = changes.obj?.previousValue?.id;
      if (previousId && previousId !== this.obj?.id) {
        this.loadReportSchedules();
        this.newReportSchedule();
      } else {
        this.loadIfReady();
      }
    }
  }

  private ensureReportTypeInitialized(): void {
    const reportType = this.reportType || 'LINE_ITEM';
    this.reportType = reportType;
    const reportTypeConfig = ADN_REPORT_TYPES[reportType];
    if (!reportTypeConfig) {
      return;
    }
    this.adnReportType = reportTypeConfig;
    this.schedulePeriods = reportTypeConfig.periods;
  }

  private loadIfReady(): void {
    if (!this.obj?.id || !this.adnReportType) {
      return;
    }
    this.loadReportSchedules();
    this.newReportSchedule();
  }

  activeSchedules(): any[] {
    return (this.reportSchedules || []).filter((schedule) => schedule.objectState === OBJECT_STATE.active);
  }

  cancelAddition(): void {
    this.newReportSchedule();
    this.showReportSchedule = false;
  }

  cancelEditing(): void {
    this.editingScheduleId = null;
  }

  startEditing(reportSchedule: any): void {
    if (!reportSchedule.modOptions) {
      afterSaveAfterInitMods(reportSchedule, this.adnTimeShifter, this.timeZone, this.scheduleLocale);
    }
    this.editingScheduleId = reportSchedule.id;
  }

  deleteSchedule(reportSchedule: any): void {
    reportSchedule.objectState = OBJECT_STATE.hidden;
    reportSchedule.$save();
  }

  editRecipients(repSchedule: any): void {
    const openModal = (salespersonId?: string, adOpsId?: string) => {
      this.reportScheduleModalService.openRecipientsModal({
        recipientUsers: repSchedule.recipientUsers,
        isNew: repSchedule.isNew,
        scheduleModel: repSchedule.isNew ? null : repSchedule,
        salespersonId,
        adOpsId
      }, (result) => {
        // Modal save settles in the click's CD turn — assigning recipientUsers
        // here flips *ngFor []→[user] on the open form (NG0100).
        deferMarkForCheck(this.cdr, () => {
          if (result?.recipientUsers) {
            repSchedule.recipientUsers = result.recipientUsers;
          }
          if (result?.validationWarnings) {
            _.set(repSchedule, 'validationWarnings', result.validationWarnings);
          }
          repSchedule.recipientEmailsSaved = result;
        });
      });
    };

    if (_.get(this.obj, 'order.id')) {
      this.Order.get({id: this.obj.order.id}, (order: any) => {
        openModal(_.get(order, 'salespersonUser.id'), _.get(order, 'adOpsUser.id'));
      });
    } else {
      openModal();
    }
  }

  updateSchedule(form: NgForm, existingSchedule?: any): void {
    const workingSchedule = existingSchedule || this.reportSchedule;
    if (this.saving || form.invalid || !this.canSubmitSchedule(workingSchedule)) {
      return;
    }

    workingSchedule.parameters = {};
    workingSchedule.parameters[this.adnReportType.idKey] = this.obj.id;
    if (this.adnReportType === ADN_REPORT_TYPES.LINE_ITEM) {
      workingSchedule.parameters.relatedLineItems = [{id: this.obj.id}];
    }

    workingSchedule.recipientEmailAddresses = _.filter(
      _.map((workingSchedule.recipientEmailAddressesCombined || '').split(','), (email) => _.trim(email)),
      (email) => email.length > 0 && EmailHelper.isEmail(email)
    );

    beforeSubmitMods(workingSchedule, this.timeZone);
    this.saving = true;

    workingSchedule.$save().then((schedule: any) => {
      this.ngZone.run(() => {
        afterSaveAfterInitMods(schedule, this.adnTimeShifter, this.timeZone, this.scheduleLocale);
        if (existingSchedule) {
          const index = _.findIndex(this.reportSchedules, ['id', existingSchedule.id]);
          if (index > -1) {
            this.reportSchedules[index] = schedule;
            this.combineEmails(this.reportSchedules[index]);
          }
          this.editingScheduleId = null;
        } else {
          this.newReportSchedule();
          this.combineEmails(schedule);
          this.reportSchedules.unshift(schedule);
          this.showReportSchedule = false;
          this.additionHappened = true;
        }
        this.saving = false;
      });
    }, () => {
      this.ngZone.run(() => {
        this.saving = false;
      });
    });
  }

  canSubmitSchedule(reportSchedule: any): boolean {
    return _.trim(reportSchedule?.name || '').length > 0 && !!reportSchedule?.reportTemplate?.id;
  }

  private loadReportSchedules(): void {
    if (!this.obj?.id || !this.adnReportType) {
      return;
    }

    this.ReportSchedule.query({
      relatedId: this.obj.id,
      relatedObjectType: this.adnReportType.objType
    }, (page: any) => {
      this.ngZone.run(() => {
        this.reportSchedules = _.filter(page.results, (schedule: any) => {
          this.combineEmails(schedule);
          return _.endsWith(_.get(schedule.parameters, this.adnReportType.idKey), this.obj.id);
        });
        this.loadRecipientDisplayNames();
      });
    });
  }

  private loadRecipientDisplayNames(): void {
    const recipUsers = _.uniq(_.flatten(_.map(
      _.filter(this.reportSchedules, (rs) => _.isArray(rs.recipientUsers) && rs.recipientUsers.length > 0),
      'recipientUsers'
    )));
    if (!recipUsers.length) {
      return;
    }

    this.User.query({
      ids: _.map(recipUsers, 'id'),
      includeHidden: true,
      includeInactive: true
    }, (page: any) => {
      this.ngZone.run(() => {
        const allUsers = page.results;
        _.forEach(this.reportSchedules, (rs) => {
          _.forEach(rs.recipientUsers, (ru: any) => {
            const match = _.find(allUsers, ['id', ru.id]);
            if (match) {
              ru.displayName = match.displayName;
            }
          });
        });
      });
    });
  }

  private newReportSchedule(): void {
    if (!this.adnReportType) {
      return;
    }

    this.reportSchedule = this.ReportSchedule.create({
      id: Uuid.generate(),
      timeZone: this.LocalNetworkProfile.get('timeZone') || 'UTC',
      currency: this.currency || this.preferredCurrency || this.LocalNetworkProfile.get('defaultCurrency') || 'EUR',
      locale: this.scheduleLocale,
      period: this.adnReportType.defaultPeriod.id,
      isNew: true
    });
    afterSaveAfterInitMods(this.reportSchedule, this.adnTimeShifter, this.timeZone, this.scheduleLocale);
  }

  private combineEmails(dataObj: any): void {
    dataObj.recipientEmailAddressesCombined = (dataObj.recipientEmailAddresses || []).join(', ');
  }
}
